import React, { useEffect, useState } from 'react'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Select from '../../../../components/common/Select/index'
import Button from '../../../../components/common/Button/index'
import ButtonCustom from '../../../../components/common/CustomButton/index'
import MiniHeader from '../../../../components/layout/MiniHeader'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import GooglePlayIcon from '../../../../components/common/Icons/GooglePlayIcon'
import { useDispatch } from 'react-redux'
import useAppSelector from '../../../../state/redux/hooks/index'
import { fetchCustomers } from '../../../../state/redux/actions/customerActions'
import { fetchAgents } from '../../../../state/redux/actions/agentActions'
import { fetchProperties } from '../../../../state/redux/actions/propertyActions'
import {
  fetchBrowserViews,
  fetchDesktopViews,
  fetchMobileViews,
  fetchOtherViews,
  fetchHourlySessions,
  fetchAllAnalytics,
  fetchWorldMap,
  fetchConversionByDevice,
  fetchSalesConversionPath,
  fetchAppDownloads,
  fetchWebsiteAnalytics,
  fetchWebsiteMetrics,
  fetchAppMetrics,
  fetchUserTypeBreakdown,
} from '../../../../state/redux/actions/dashboardActions'
import { fetchAllSessions } from '../../../../state/redux/actions/sessionActions.js'
import PieChartGraph from '../../../../components/charts/Piechart'
import PieChartGraphCustom from '../../../../components/charts/PiechartCustom'
import DonutChart from '../../../../components/charts/DonutChart'
import DonutChartUsers from '../../../../components/charts/DonutChartUsers'
import ViewingsBar from '../../../../components/charts/ViewingsBarOverview'
import MessageCount from '../../../../components/charts/MessageCount'
import MavenBuyerChart from '../../../../components/charts/MavenBuyerChart'
import { isNew } from '../../../../shared/utils/ageUtils'
import { ListItemAvatar, Avatar } from '@mui/material'
import { formatDistanceToNowStrict } from 'date-fns'
import { formatTime } from '../../../../shared/utils/dateUtils'
import { CiMobile4 } from 'react-icons/ci'
import { FaDesktop } from 'react-icons/fa6'
import { RiAppleFill } from 'react-icons/ri'
import { IoIosArrowDown } from 'react-icons/io'
import { Link } from 'react-router-dom'
import WorldMapCard from '../WorldMap/WorldMap'
import Map from '../../../../components/common/Map/Map'
import ProximityMap from '../../../../components/common/Map/ProximityMap'
import WebVisitors from '../../../../components/common/Map/WebVisitors'
import { getCountryInfo } from '../../../../shared/utils/countryUtils'

const Overview = () => {
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [filteredData, setFilteredData] = useState({
    agents: [],
    customers: [],
    properties: [],
  })
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false) // Track if we're actively filtering

  const dispatch = useDispatch()

  const { agents, loading: agentsLoading, error: agentsError } = useAppSelector(state => state.agent)
  const { customers, loading: customersLoading, error: customersError } = useAppSelector(state => state.customer)
  const { properties, loading: propertiesLoading, error: propertiesError } = useAppSelector(state => state.property)

  const {
    browserViews,
    desktopViews,
    mobileViews,
    otherViews,
    hourlySessions,
    allAnalytics,
    worldMap,
    conversionByDevice,
    salesConversionPath,
    appDownloads,
    websiteAnalytics,
    websiteMetrics,
    appMetrics,
    userTypeBreakdown,
    loading: dashboardLoading,
    error: dashboardError,
  } = useAppSelector(state => state.dashboard)

  const {
    sessions,
    loading: sessionLoading,
    error: sessionError,
  } = useAppSelector(state => state.session)

  const loading = agentsLoading || customersLoading || propertiesLoading || dashboardLoading || sessionLoading
  const error = agentsError || customersError || propertiesError || dashboardError || sessionError

  // Fetch all data unfiltered on component mount
  useEffect(() => {
    dispatch(fetchAgents())
    dispatch(fetchCustomers())
    dispatch(fetchProperties())
    dispatch(fetchConversionByDevice())
    dispatch(fetchSalesConversionPath())
    dispatch(fetchAppDownloads())
    dispatch(fetchWebsiteAnalytics())
    dispatch(fetchWebsiteMetrics())
    dispatch(fetchAppMetrics())
    dispatch(fetchUserTypeBreakdown())
    dispatch(fetchAllSessions())
  }, [dispatch])

  useEffect(() => {
    if (selectedFilter === 'all' || selectedFilter === 'website') {
      if (selectedFilter === 'website') {
        dispatch(fetchBrowserViews())
        dispatch(fetchDesktopViews())
        dispatch(fetchMobileViews())
        dispatch(fetchOtherViews())
        dispatch(fetchHourlySessions())
        dispatch(fetchAllAnalytics())
      }
      dispatch(fetchWorldMap())
    }
  }, [selectedFilter, dispatch])

  // Filter data by date range using updatedAt field
  const filterDataByDate = (data, startDate, endDate) => {
    if (!startDate || !endDate || !data || !Array.isArray(data)) {
      return data || []
    }

    return data.filter(item => {
      const itemDate = new Date(item.updatedAt || item.createdAt || item.date || item.timestamp)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return itemDate >= start && itemDate <= end
    })
  }

  // Handle filter change from MiniHeader - ONLY when buttons are clicked
  const handleFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      setFilteredData({
        agents: agents || [],
        customers: customers || [],
        properties: properties || [],
      })
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)

    // Filter all data arrays
    const filteredAgents = filterDataByDate(agents, dateRange.startDate, dateRange.endDate)
    const filteredCustomers = filterDataByDate(customers, dateRange.startDate, dateRange.endDate)
    const filteredProperties = filterDataByDate(properties, dateRange.startDate, dateRange.endDate)

    setFilteredData({
      agents: filteredAgents,
      customers: filteredCustomers,
      properties: filteredProperties,
    })
  }

  // Initialize filtered data when original data changes - but don't filter unless actively filtering
  useEffect(() => {
    if (isFiltering && dateFilter) {
      // We're actively filtering, apply existing date filter to new data
      const filteredAgents = filterDataByDate(agents, dateFilter.startDate, dateFilter.endDate)
      const filteredCustomers = filterDataByDate(customers, dateFilter.startDate, dateFilter.endDate)
      const filteredProperties = filterDataByDate(properties, dateFilter.startDate, dateFilter.endDate)

      setFilteredData({
        agents: filteredAgents,
        customers: filteredCustomers,
        properties: filteredProperties,
      })
    } else {
      // No active filtering - show all data
      setFilteredData({
        agents: agents || [],
        customers: customers || [],
        properties: properties || [],
      })
    }
  }, [agents, customers, properties, dateFilter, isFiltering])

  // Use filtered or original data based on filtering state
  const displayAgents = isFiltering ? filteredData.agents : agents || []
  const displayCustomers = isFiltering ? filteredData.customers : customers || []
  const displayProperties = isFiltering ? filteredData.properties : properties || []

  const allUsers = [...displayAgents, ...displayCustomers]
  const allUsersData = [
    { name: 'Brokers', value: displayAgents.length, fill: '#14b8a6' },
    { name: 'Customers', value: displayCustomers.length, fill: '#343a40' },
  ]

  const activeBrokers = [
    {
      name: 'Active Users',
      value: displayAgents.filter(agent => agent.status === 'active' || agent.status === 'ACTIVE').length,
      fill: '#1a539b',
    },
    {
      name: 'Non-Active Users',
      value: displayAgents.filter(agent => agent.status !== 'active' && agent.status !== 'ACTIVE').length,
      fill: '#ff8c00',
    },
  ]

  const activeCustomers = [
    {
      name: 'Active Users',
      value: displayCustomers.filter(customer => customer.status === 'ACTIVE').length,
      fill: '#14b8a6',
    },
    {
      name: 'Non-Active Users',
      value: displayCustomers.filter(customer => customer.status !== 'ACTIVE').length,
      fill: '#343a40',
    },
  ]

  // Filter new users - respect date filter only if actively filtering
  const newUsers = allUsers.filter(user => {
    if (isFiltering && dateFilter) {
      // If actively filtering, check both isNew and date filter
      const itemDate = new Date(user.updatedAt || user.createdAt)
      const start = new Date(dateFilter.startDate)
      const end = new Date(dateFilter.endDate)
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
      return isNew(user.createdAt) && itemDate >= start && itemDate <= end
    }
    // If not actively filtering, just check isNew
    return isNew(user.createdAt)
  })

  const sortedNewUsers = [...newUsers].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.signupDate).getTime()
    const dateB = new Date(b.createdAt || a.signupDate).getTime()
    return dateB - dateA
  })

  // Filter new properties - respect date filter only if actively filtering
  const newProperties = displayProperties.filter(property => {
    if (isFiltering && dateFilter) {
      const itemDate = new Date(property.updatedAt || property.createdAt)
      const start = new Date(dateFilter.startDate)
      const end = new Date(dateFilter.endDate)
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
      return isNew(property.createdAt) && itemDate >= start && itemDate <= end
    }
    return isNew(property.createdAt)
  })

  const handleChange = e => {
    setSelectedFilter(e.target.value)
  }

  const extractData = responseData => {
    if (!responseData) return []
    if (Array.isArray(responseData)) return responseData
    if (responseData.data && Array.isArray(responseData.data)) return responseData.data
    return []
  }

  const extractSummary = responseData => {
    if (!responseData) return null
    if (responseData.summary) return responseData.summary
    return null
  }

  const getBrowserViewsChartData = () => {
    const browserData = extractData(browserViews)
    const browserSummary = extractSummary(browserViews)

    if (!browserSummary || !browserSummary.browserBreakdown) {
      return []
    }

    const browserColors = {
      Chrome: '#ff9800',
      Safari: '#2196f3',
      Edge: '#00bcd4',
      Firefox: '#ff5722',
      Unknown: '#9e9e9e',
      Other: '#795548',
    }

    return Object.entries(browserSummary.browserBreakdown).map(([browser, count]) => ({
      name: browser || 'Unknown',
      value: count || 0,
      fill: browserColors[browser] || browserColors['Other'],
    }))
  }

  const processSessionViewsData = (responseData, groupBy = 'deviceType') => {
    const data = extractData(responseData)
    if (data.length === 0) return []

    const grouped = data.reduce((acc, item) => {
      const key = item[groupBy] || 'Unknown'
      if (!acc[key]) {
        acc[key] = {
          name: key,
          count: 0,
          views: 0,
        }
      }
      acc[key].count += 1
      acc[key].views += 1
      return acc
    }, {})

    const result = Object.values(grouped)
    const total = result.reduce((sum, item) => sum + item.count, 0)

    return result.map(item => ({
      ...item,
      percentage: total > 0 ? ((item.count / total) * 100).toFixed(1) : 0,
    }))
  }

  // Combine all ORIGINAL data for MiniHeader filtering (not pre-filtered data)
  const allDataForFiltering = [...(agents || []), ...(customers || []), ...(properties || [])]

  return (
    <>
      <MiniHeader title="Overview" data={allDataForFiltering} onFilterChange={handleFilterChange} />
      <div className="ml-[250px] p-8 max-w-7xl mx-auto">
        {loading && <LoadingIndicator />}
        {error && <ErrorState error={error} />}
        {!loading && !error && (allUsers.length || displayProperties.length) > 0 && (
          <>
            <div className="flex justify-end mr-6">
              <div>
                <Select
                  id="filterType"
                  name="filterType"
                  value={selectedFilter}
                  onChange={handleChange}
                  icon={<IoIosArrowDown />}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'website', label: 'Website' },
                    { value: 'conversion_rate', label: 'Conversion rate' },
                  ]}
                  required
                  className="mb-2"
                />
              </div>
            </div>

            {/* Show current filter info only when actively filtering */}
            {isFiltering && dateFilter && (
              <div className="mb-4 p-2 bg-teal-50 border border-teal-200 rounded-md">
                <Typography variant="caption" className="text-teal-700">
                  Showing data from {new Date(dateFilter.startDate).toLocaleDateString()} to{' '}
                  {new Date(dateFilter.endDate).toLocaleDateString()} ({allUsers.length} users,{' '}
                  {displayProperties.length} properties)
                </Typography>
              </div>
            )}

            {/* Rest of your component remains the same... */}
            {/* ALL */}
            {selectedFilter === 'all' && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card title={'Web analytics'} className="p-4">
                  <Typography className="text-sm mt-5">
                    Conversion rate: <span className="text-primary font-semibold">{websiteAnalytics?.conversionRate || 0}%</span>
                  </Typography>

                  <div className="grid grid-cols-2 gap-4 px-2 py-2 mt-5">
                    <div className="flex items-center gap-2">
                      <Button className="px-3 py-1 text-sm rounded-xl">{websiteAnalytics?.visitors || 0}</Button>
                      <Typography className="text-xs">Visitors</Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button className="px-3 py-1 text-sm rounded-xl">{websiteAnalytics?.pageViews || 0}</Button>
                      <Typography className="text-xs">Page Views</Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button className="px-3 py-1 text-sm rounded-xl">{websiteAnalytics?.downloads || 0}</Button>
                      <Typography className="text-xs">Downloads</Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button className="px-3 py-1 text-sm rounded-xl">{websiteAnalytics?.clicks || 0}</Button>
                      <Typography className="text-xs">Clicks</Typography>
                    </div>
                  </div>
                </Card>

                <Card title={'Conversion rate by device'} className="p-4">
                  {conversionByDevice && Array.isArray(conversionByDevice) && conversionByDevice.length > 0 ? (
                    (() => {
                      const mobileDevice = conversionByDevice.find(device => device.deviceType === 'mobile')
                      const desktopDevice = conversionByDevice.find(device => device.deviceType === 'desktop')
                      const displayDevices = [mobileDevice, desktopDevice].filter(Boolean)
                      return displayDevices.length > 0 ? (
                        <>
                          {/* Device Icons and Labels */}
                          <div className="columns-2 divide-x-2 mt-10 py-4 px-2">
                            <div className="flex col-span-1 justify-center items-center">
                              {mobileDevice && (
                                <>
                                  <CiMobile4 className="text-purple-700 bg-purple-200 mr-1" />
                                  <Typography variant="h5" className="text-gray-500" align="center">
                                    Mobile
                                  </Typography>
                                </>
                              )}
                            </div>
                            <div className="flex col-span-1 justify-center items-center">
                              {desktopDevice && (
                                <>
                                  <FaDesktop className="text-primary bg-teal-500/10 mr-1" />
                                  <Typography variant="h5" className="text-gray-500" align="center">
                                    Desktop
                                  </Typography>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Conversion Rates with Vs in between */}
                          <div className="columns-3 justify-center items-center px-2">
                            {mobileDevice && (
                              <Typography variant="h2" align="right">
                                {mobileDevice.conversionRate}%
                              </Typography>
                            )}
                            <Typography variant="body" align="center" className="text-gray-700">
                              Vs
                            </Typography>
                            {desktopDevice && (
                              <Typography variant="h2" align="left">
                                {desktopDevice.conversionRate}%
                              </Typography>
                            )}
                          </div>

                          {/* Visitor Counts */}
                          <div className="columns-2 divide-x-2 py-4 px-2">
                            {mobileDevice && (
                              <Typography variant="h6" align="center">
                                {mobileDevice.total} Visitors
                              </Typography>
                            )}
                            {desktopDevice && (
                              <Typography variant="h6" align="center" className="text-gray-700">
                                {desktopDevice.total} Visitors
                              </Typography>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-32">
                          <Typography variant="body" className="text-gray-500">
                            No mobile or desktop data available
                          </Typography>
                        </div>
                      )
                    })()
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <Typography variant="body" className="text-gray-500">
                        {dashboardLoading ? 'Loading conversion data...' : 'No conversion data available'}
                      </Typography>
                    </div>
                  )}
                </Card>

                <Card className="">
                  <div className="w-full">
                    <div className="flex justify-start p-4">
                      <Typography
                        variant="h6"
                        className="text-primary border border-primary bg-teal-600/10 px-2 py-2 rounded-xl"
                      >
                        All Users {allUsers.length}
                      </Typography>
                    </div>
                    <PieChartGraphCustom data={allUsersData} fill={allUsersData.fill} />
                  </div>
                </Card>

                <Card title={'Application Downloads'} className="p-6 rounded-lg shadow-lg">
                  <div className="grid grid-cols-2 divide-x-2 py-4">
                    <div className="flex flex-col items-center">
                      <Typography variant="body" className="text-gray-700">
                        Broker
                      </Typography>
                      <Typography variant="body" className="font-semibold text-lg">
                        25
                      </Typography>
                    </div>
                    <div className="flex flex-col items-center">
                      <Typography variant="body" className="text-gray-700">
                        Customer
                      </Typography>
                      <Typography variant="body" className="font-semibold text-lg">
                        50
                      </Typography>
                    </div>
                  </div>
                  <div className="text-center py-2">
                    <Typography variant="body" className="text-center text-gray-700">
                      Vs
                    </Typography>
                  </div>
                  <div className="grid grid-cols-2 divide-x-2 py-4">
                    <div className="flex flex-col gap-y-2">
                      <ButtonCustom 
                        variant="black" 
                        size="small" 
                        className="flex items-center justify-center p-1 cursor-pointer hover:bg-gray-100 transition-colors"
                        
                      >
                        <div className="flex gap-1 items-center">
                          <RiAppleFill className="text-gray-500 text-md" />
                          <Typography variant="body2" style={{ fontSize: '0.55rem' }}>
                            Apple Store - <span className="font-bold">{appDownloads?.byPlatform?.iOS || 0}</span>
                          </Typography>
                        </div>
                      </ButtonCustom>
                      <ButtonCustom 
                        variant="black" 
                        size="small" 
                        className="flex items-center justify-center p-1 cursor-pointer hover:bg-gray-100 transition-colors"
                        
                      >
                        <div className="flex gap-1 items-center">
                          <GooglePlayIcon size={14} />
                          <Typography variant="body2" style={{ fontSize: '0.55rem' }}>
                            Google Play - <span className="font-bold">{appDownloads?.byPlatform?.Android || 0}</span>
                          </Typography>
                        </div>
                      </ButtonCustom>
                    </div>
                    <div className="flex flex-col gap-y-2 pl-1">
                      <ButtonCustom variant="black" size="small" className="flex items-center justify-center p-1">
                        <div className="flex gap-1 items-center">
                          <i className="fa-solid fa-globe text-gray-500 text-md"></i>
                          <Typography variant="body2" style={{ fontSize: '0.55rem' }}>
                            Web - <span className="font-bold">{appDownloads?.byPlatform?.Web || 0}</span>
                          </Typography>
                        </div>
                      </ButtonCustom>
                      {/* <ButtonCustom variant="black" size="small" className="flex items-center justify-center p-1">
                        <div className="flex gap-1 items-center">
                          <i className="fa-brands fa-google-play text-gray-500 text-md"></i>
                          <Typography variant="body" className="text-xs">
                            Google Play <span className="font-semibold">15</span>
                          </Typography>
                        </div>
                      </ButtonCustom> */}
                    </div>
                  </div>
                </Card>
                <Card title={'Sale Conversion Pathway'} className="p-4">
                  <div className="grid grid-cols-2 gap-4 px-2 mt-5">
                    {[
                      { key: 'leads', label: 'Leads' },
                      { key: 'saves', label: 'Saves' },
                      { key: 'rented', label: 'Rented' },
                      { key: 'requests', label: 'Requests' },
                      { key: 'totalListings', label: 'Total Listings' },
                      { key: 'offers', label: 'Offers' },
                      { key: 'sold', label: 'Sold' },
                      { key: 'clients', label: 'Clients' },
                    ].map(({ key, label }) => {
                      const dataArray = salesConversionPath?.data || salesConversionPath || []
                      const item = Array.isArray(dataArray)
                        ? dataArray.find(d => d.name === key)
                        : null
                      const value = item?.value ?? 0

                      return (
                        <div className="flex items-center gap-2" key={key}>
                          <Button className="px-3 py-1 text-sm rounded-xl">
                            {value}
                          </Button>
                          <Typography className="text-xs">{label}</Typography>
                        </div>
                      )
                    })}
                  </div>
                </Card>

                <Card className="p-6 col-span-2">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h4 className="text-md font-bold text-black mb-2">
                        Website visitors by location
                      </h4>
                      <Typography variant="h5" className="text-md font-bold text-gray">
                        {worldMap?.data?.totalGlobalUsers || '0'}
                      </Typography>
                    </div>
                    <Link to={'/analytics'}>
                      <Typography className="text-primary font-semibold text-sm underline underline-offset-2">
                        Learn more
                      </Typography>
                    </Link>
                  </div>

                  <div className="flex gap-8">
                    {/* Map Section - Takes up more space (about 70%) */}
                    <div className="flex-[4]">
                      <Map
                        center={{ lat: 20, lng: 0 }}
                        zoom={2}
                        height={'320px'}
                        markers={(worldMap?.data?.countries || []).map((item, index) => {
                          // Get country info dynamically using our utility
                          const countryInfo = getCountryInfo(item.country)

                          return {
                            id: index,
                            title: item.country,
                            label: `${item.count}`,
                            countryCode: countryInfo?.code || item.country?.slice(0, 2).toLowerCase(),
                            flag: item.flag || countryInfo?.flag
                          }
                        })}
                      />
                    </div>

                    {/* Countries Legend - Smaller width (about 30%) */}
                    <div className="flex-[1] flex flex-col gap-2 items-start">
                      {(worldMap?.data?.countries || []).slice(0, 6).map((item, index) => {
                        // Get country info dynamically using our utility
                        const countryInfo = getCountryInfo(item.country)
                        const countryCode = countryInfo?.code || item.country?.slice(0, 2).toLowerCase()
                        const countryFlag = item.flag || countryInfo?.flag

                        return (
                          <div key={index} className="flex flex-col gap-1 w-full">
                            {/* Flag and Country Name */}
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0 relative">
                                <img
                                  src={`https://flagcdn.com/16x12/${countryCode}.png`}
                                  alt={item.country}
                                  className="w-4 h-3 rounded-sm border border-gray-200"
                                  onError={(e) => {
                                    // Fallback to emoji flag
                                    e.target.style.display = 'none'
                                    if (e.target.nextElementSibling && countryFlag) {
                                      e.target.nextElementSibling.style.display = 'inline-block'
                                    }
                                  }}
                                />
                                {countryFlag && (
                                  <span
                                    className="text-sm"
                                    style={{ display: 'none' }}
                                  >
                                    {countryFlag}
                                  </span>
                                )}
                              </div>
                              <Typography variant="caption" className="text-xs text-gray-800 whitespace-nowrap">
                                {item.country}
                              </Typography>
                            </div>

                            {/* Progress Bar and Percentage in same row */}
                            <div className="flex items-center gap-2">
                              {/* Progress Bar - takes half width */}
                              <div className="flex-1">
                                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-1.5 bg-primary rounded-full transition-all duration-300"
                                    style={{ width: `${Math.min(item.percentage || 0, 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                              {/* Percentage - takes remaining half */}
                              <div className="flex-1">
                                <Typography variant="caption" className="text-xs text-gray-600 text-left">
                                  {item.percentage?.toFixed(2)}%
                                </Typography>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </Card>

                <Card title={'Users(Broker)'} className="p-4">
                  <DonutChartUsers data={activeBrokers} />
                </Card>

                <Card className="p-4 col-span-2">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-bold text-black mb-2">
                      New listings <span className="text-gray-400">{newProperties.length}</span>
                    </h4>
                    <Typography variant="body2" className="text-gray-400 mt-5">
                      {newProperties.length > 6
                        ? `${newProperties.length - 6}+ ${dateFilter ? 'in range' : 'today'}`
                        : `${newProperties.length} ${dateFilter ? 'in range' : 'today'}`}
                    </Typography>
                  </div>

                  {/* Listings */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {newProperties.slice(0, 6).map(property => (
                      <div key={property._id} className="flex items-center space-x-3">
                        {/* Thumbnail */}
                        <img
                          src={
                            property.images && property.images.length > 0
                              ? property.images[0]
                              : '/assets/images/house.jpg'
                          }
                          alt={property.property_title}
                          className="w-16 h-16 object-cover rounded-md"
                        />

                        {/* Info */}
                        <div className="flex flex-col min-w-0">
                          <Typography
                            variant="body"
                            className="font-medium truncate max-w-[120px] md:max-w-[150px]"
                            title={property.property_title}
                          >
                            {property.property_title}
                          </Typography>
                          <Typography
                            variant="body2"
                            className="text-gray-500 truncate max-w-[120px] md:max-w-[150px]"
                            title={`Listed ${formatTime(property.createdAt)}`}
                          >
                            Listed {formatTime(property.createdAt)}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer link */}
                  <div className="flex justify-end mt-3">
                    <Link to={'/properties'}>
                      <Typography className="text-primary font-semibold text-sm">View all</Typography>
                    </Link>
                  </div>
                </Card>

                <Card title={'Users(Customer)'} className="p-4">
                  <DonutChartUsers data={activeCustomers} />
                </Card>

                <Card className="p-4">
                  {/* Header */}
                  <div className="flex justify-between mb-4">
                    <h4 className="text-md font-bold text-black mb-2">New Users</h4>
                    <Link to={'/users'}>
                      <Typography className="text-primary font-bold underline underline-offset-2">See more</Typography>
                    </Link>
                  </div>

                  {/* User List */}
                  <div>
                    {sortedNewUsers.slice(0, 2).map(user => (
                      <div key={user._id} className="flex items-center space-x-3 py-2">
                        <ListItemAvatar>
                          <Avatar alt="Profile" src={user.image} />
                        </ListItemAvatar>
                        <div className="flex flex-col w-full">
                          <Typography className="text-dark">{user.full_name}</Typography>
                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body"
                              className="text-primary border border-primary bg-teal-600/10 px-2 py-1 rounded-lg"
                            >
                              {user.realtor !== null ? 'Broker' : 'Agent'}
                            </Typography>
                            <div className="flex flex-col text-right">
                              <Typography variant="caption">Joined,</Typography>
                              <Typography variant="caption" className="text-gray-500">
                                {formatTime(user.createdAt)}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Website */}
            {selectedFilter === 'website' && (
              <div className="space-y-6">
                {/* Top metrics row */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Card className="p-4">
                    <div>
                      <Typography className="text-gray-700 text-sm">Users</Typography>
                      <div className="flex justify-between items-center mt-2">
                        <Typography className="text-2xl font-semibold">{sessions?.users || 0}</Typography>
                        <Typography className={`text-sm ${sessions?.usersGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {sessions?.usersGrowth || 0}%
                          <i className={`fa-solid ml-1 ${sessions?.usersGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                        </Typography>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div>
                      <Typography className="text-gray-700 text-sm">Sessions</Typography>
                      <div className="flex justify-between items-center mt-2">
                        <Typography className="text-2xl font-semibold">{sessions?.sessions || 0}</Typography>
                        <Typography className={`text-sm ${sessions?.sessionsGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {sessions?.sessionsGrowth || 0}%
                          <i className={`fa-solid ml-1 ${sessions?.sessionsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                        </Typography>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div>
                      <Typography className="text-gray-700 text-sm">Session duration</Typography>
                      <div className="flex justify-between items-center mt-2">
                        <Typography className="text-2xl font-semibold">{sessions?.sessionDuration || 0}</Typography>
                        <Typography className={`text-sm ${sessions?.sessionDurationGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {sessions?.sessionDurationGrowth || 0}%
                          <i className={`fa-solid ml-1 ${sessions?.sessionDurationGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                        </Typography>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div>
                      <Typography className="text-gray-700 text-sm">Page views</Typography>
                      <div className="flex justify-between items-center mt-2">
                        <Typography className="text-2xl font-semibold">{sessions?.pageViews || 0}</Typography>
                        <Typography className={`text-sm ${sessions?.pageViewsGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {sessions?.pageViewsGrowth || 0}%
                          <i className={`fa-solid ml-1 ${sessions?.pageViewsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                        </Typography>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div>
                      <Typography className="text-gray-700 text-sm">Unique Visitors</Typography>
                      <div className="flex justify-between items-center mt-2">
                        <Typography className="text-2xl font-semibold">{sessions?.uniqueVisitors || 0}</Typography>
                        <Typography className={`text-sm ${sessions?.uniqueVisitorsGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {sessions?.uniqueVisitorsGrowth || 0}%
                          <i className={`fa-solid ml-1 ${sessions?.uniqueVisitorsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                        </Typography>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Map and Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left side - Map */}
                  <Card title={'Top Countries'} className="p-4">
                    <Map
                      center={{ lat: 40, lng: 30 }}
                      zoom={0}
                      height={'500px'}
                      markers={(worldMap?.data?.topPerformingCountries || []).map((item, index) => {
                        // Get country info dynamically using our utility
                        const countryInfo = getCountryInfo(item.country)

                        return {
                          id: index,
                          title: item.country,
                          label: `${item.count}`,
                          countryCode: countryInfo?.code || item.country?.slice(0, 2).toLowerCase(),
                          flag: item.flag || countryInfo?.flag
                        }
                      })}
                    />
                  </Card>

                  {/* Right side - Charts */}
                  <div className="space-y-4">
                    <Card title={'Views'} className="p-4">
                      <MessageCount
                        data={allAnalytics}
                        title="Hourly Views Analysis"
                        strokeColor="#7C3AED"
                        showTitle={false}
                        valueKey="sessions"
                        tooltipLabel="Views"
                        height={200}
                      />
                    </Card>
                    <Card title={'Sessions'} className="p-4">
                      <MessageCount
                        data={hourlySessions}
                        title="Hourly Sessions Analysis"
                        strokeColor="#7C3AED"
                        showTitle={false}
                        height={200}
                      />
                    </Card>
                  </div>
                </div>

                {/* Bottom row - Browser, Mobile, Desktop, Other Views */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card title={'Browser Views'} className="p-4">
                    <DonutChart data={browserViews} />
                  </Card>

                  <Card title={'Mobile Views'} className="p-4">
                    <ViewingsBar
                      data={mobileViews}
                      viewType="mobile"
                      title="Mobile Views"
                      groupBy="month"
                      showTitle={false}
                      height={250}
                      barSize={20}
                    />
                  </Card>

                  <Card title={'Desktop Views'} className="p-4">
                    <ViewingsBar
                      data={desktopViews}
                      viewType="desktop"
                      title="Desktop Views"
                      groupBy="month"
                      strokeColor="#7C3AED"
                      yAxisFormat="k"
                      height={250}
                      showGrid={true}
                      barSize={20}
                      showTitle={false}
                    />
                  </Card>

                  <Card title={'Other Views'} className="p-4">
                    <ViewingsBar
                      data={otherViews}
                      viewType="other"
                      title="Other Views"
                      strokeColor="#7C3AED"
                      groupBy="month"
                      showTitle={false}
                      height={250}
                      barSize={20}
                    />
                  </Card>
                </div>
              </div>
            )}
            {/*Conversion Rate */}
            {selectedFilter === 'conversion_rate' && (
              <div className="">
                <div className="">
                  <Typography variant="h3" className="mt-2">
                    Website
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Visits</Typography>
                        <div className="flex justify-between">
                          <Typography>{websiteMetrics?.visits || 0}</Typography>
                          <Typography className={`${websiteMetrics?.visitsGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {websiteMetrics?.visitsGrowth || 0}%
                            <i className={`fa-solid ${websiteMetrics?.visitsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Clicks</Typography>
                        <div className="flex justify-between">
                          <Typography>{websiteMetrics?.clicks || 0}</Typography>
                          <Typography className={`${websiteMetrics?.clicksGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {websiteMetrics?.clicksGrowth || 0}%
                            <i className={`fa-solid ${websiteMetrics?.clicksGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Downloads</Typography>
                        <div className="flex justify-between">
                          <Typography>{websiteMetrics?.downloads || 0}</Typography>
                          <Typography className={`${websiteMetrics?.downloadsGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {websiteMetrics?.downloadsGrowth || 0}%
                            <i className={`fa-solid ${websiteMetrics?.downloadsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
                <div className="">
                  <Typography variant="h3" className="mt-2">
                    App
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Downloads</Typography>
                        <div className="flex justify-between">
                          <Typography>{appMetrics?.downloads || 0}</Typography>
                          <Typography className={`${appMetrics?.downloadsGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {appMetrics?.downloadsGrowth || 0}%
                            <i className={`fa-solid ${appMetrics?.downloadsGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Registered</Typography>
                        <div className="flex justify-between">
                          <Typography>{appMetrics?.registered || 0}</Typography>
                          <Typography className={`${appMetrics?.registeredGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {appMetrics?.registeredGrowth || 0}%
                            <i className={`fa-solid ${appMetrics?.registeredGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div>
                        <Typography className="text-gray-700">Clicks</Typography>
                        <div className="flex justify-between">
                          <Typography>{appMetrics?.clicks || 0}</Typography>
                          <Typography className={`${appMetrics?.clicksGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {appMetrics?.clicksGrowth || 0}%
                            <i className={`fa-solid ${appMetrics?.clicksGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                          </Typography>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4 col-span-2">
                       <div>
                         <div className="flex justify-between items-center mb-4">
                           <Typography variant="h5" className="text-gray-700">
                             Maven Buyer
                           </Typography>
                           <div className="text-right">
                             <Typography variant="h6">{userTypeBreakdown?.mavenBuyer || 0}</Typography>
                             <Typography className={`${userTypeBreakdown?.mavenBuyerGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                               {userTypeBreakdown?.mavenBuyerGrowth || 0}%
                               <i className={`fa-solid ${userTypeBreakdown?.mavenBuyerGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                             </Typography>
                           </div>
                         </div>
                         <MavenBuyerChart data={userTypeBreakdown?.chartData || []} />
                       </div>
                     </Card>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default Overview